import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Search, ChevronUp, ChevronDown, ChevronsUpDown, ChevronLeft, ChevronRight,
  ExternalLink, Loader2, X, Tag, Info,
} from 'lucide-react';
import { Navbar } from './Navbar';
import { Footer } from './Footer';
import { useLanguage } from '../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  'webspace','vserver','dedi','gameserver','teamspeak',
  'discordbots','musicbots','domains','other',
];

/* ── Helpers ─────────────────────────────────────────────────────────────── */
function SortIcon({ col, cfg }) {
  if (cfg.key !== col) return <ChevronsUpDown className="w-3 h-3 text-slate-600 flex-shrink-0" />;
  return cfg.dir === 'asc'
    ? <ChevronUp   className="w-3 h-3 text-cyan-400 flex-shrink-0" />
    : <ChevronDown className="w-3 h-3 text-cyan-400 flex-shrink-0" />;
}

function ProviderLogo({ name, logo }) {
  const [err, setErr] = useState(false);
  if (logo && !err)
    return (
      <img src={logo} alt={name} onError={() => setErr(true)}
        className="w-5 h-5 rounded object-contain bg-white/5 border border-slate-700 flex-shrink-0" />
    );
  return (
    <span className="w-5 h-5 rounded bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-[9px] font-bold text-cyan-400 flex-shrink-0">
      {name?.slice(0,2).toUpperCase()}
    </span>
  );
}

function Cell({ val }) {
  return (
    <span className={val ? 'text-slate-300' : 'text-slate-600'}>
      {val || '–'}
    </span>
  );
}

/* ── Main Component ─────────────────────────────────────────────────────── */
export default function ProductsPage() {
  const { t } = useLanguage();
  const tp = t.productsPage;
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');
  const [category, setCategory] = useState('all');
  const [provider, setProvider] = useState('all');
  const [sort,        setSort]        = useState({ key: 'price', dir: 'asc' });
  const [fullContent, setFullContent] = useState(true);
  const [page,        setPage]        = useState(1);
  const [perPage,     setPerPage]     = useState(10);

  // Reset to page 1 whenever filters or sort change
  useEffect(() => { setPage(1); }, [category, provider, search, sort]);

  useEffect(() => {
    axios.get(`${API}/products`)
      .then(r => setProducts(r.data))
      .finally(() => setLoading(false));
  }, []);

  const handleSort = (key) => {
    setSort(s => ({ key, dir: s.key === key && s.dir === 'asc' ? 'desc' : 'asc' }));
  };

  const usedCats = useMemo(() =>
    CATEGORIES.filter(c => products.some(p => p.category === c)), [products]);

  const usedProviders = useMemo(() => {
    const seen = new Set();
    return products
      .filter(p => { if (seen.has(p.hosting_id)) return false; seen.add(p.hosting_id); return true; })
      .map(p => ({ id: p.hosting_id, name: p.provider_name, logo: p.provider_logo }));
  }, [products]);

  const filtered = useMemo(() => {
    let list = [...products];
    if (category !== 'all') list = list.filter(p => p.category === category);
    if (provider !== 'all') list = list.filter(p => p.hosting_id === provider);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(p =>
        p.name?.toLowerCase().includes(q) ||
        p.provider_name?.toLowerCase().includes(q) ||
        p.description?.toLowerCase().includes(q)
      );
    }
    list.sort((a, b) => {
      let av = a[sort.key], bv = b[sort.key];
      if (sort.key === 'price') { av = Number(av) || 0; bv = Number(bv) || 0; }
      if (typeof av === 'string') return sort.dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return sort.dir === 'asc' ? av - bv : bv - av;
    });
    return list;
  }, [products, category, provider, search, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const safePage   = Math.min(page, totalPages);
  const pageFrom   = (safePage - 1) * perPage;
  const paginated  = filtered.slice(pageFrom, pageFrom + perPage);

  /* Sortable header button */
  const Th = ({ label, col, className = '' }) => (
    <th className={`px-3 py-3 text-left ${className}`}>
      <button onClick={() => handleSort(col)}
        data-testid={`sort-${col}`}
        className={`flex items-center gap-1 text-xs font-semibold uppercase tracking-wider transition-colors whitespace-nowrap ${
          sort.key === col ? 'text-cyan-400' : 'text-slate-400 hover:text-white'
        }`}>
        {label}<SortIcon col={col} cfg={sort} />
      </button>
    </th>
  );

  return (
    <div className="min-h-screen bg-[#020817] flex flex-col">
      <Navbar />

      {/* Hero */}
      <section className="pt-28 pb-8 px-4 md:px-8 max-w-7xl mx-auto w-full">
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 mb-4">
          <Tag className="w-3 h-3" />{tp.subtitle}
        </span>
        <h1 className="text-4xl sm:text-5xl font-extrabold text-white tracking-tight mb-1"
          data-testid="products-page-title">
          {tp.title}
        </h1>
        {!loading && (
          <p className="text-sm text-slate-500 mt-1" data-testid="products-count">
            {tp.productsCount(filtered.length)}
          </p>
        )}
      </section>

      {/* Sticky filter bar */}
      <div className="sticky top-16 z-30 bg-[#020817]/95 backdrop-blur border-b border-slate-800/60">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex flex-wrap items-center gap-3">

          {/* Search */}
          <div className="relative min-w-[180px] flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder={tp.searchPlaceholder}
              data-testid="products-search"
              className="w-full pl-9 pr-8 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-500 transition-colors" />
            {search && (
              <button onClick={() => setSearch('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Category chips */}
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-xs text-slate-500 uppercase tracking-wider mr-1">{tp.filterLabel}</span>
            <button onClick={() => setCategory('all')} data-testid="filter-all"
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                category === 'all'
                  ? 'bg-cyan-500 border-cyan-500 text-slate-950'
                  : 'bg-transparent border-slate-700 text-slate-400 hover:border-slate-500 hover:text-white'
              }`}>{tp.filterAll}
            </button>
            {usedCats.map(cat => (
              <button key={cat} onClick={() => setCategory(cat)} data-testid={`filter-${cat}`}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                  category === cat
                    ? 'bg-cyan-500 border-cyan-500 text-slate-950'
                    : 'bg-transparent border-slate-700 text-slate-400 hover:border-slate-500 hover:text-white'
                }`}>
                {t.categories?.[cat] || cat}
              </button>
            ))}
          </div>

          {/* Provider dropdown */}
          {usedProviders.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500 uppercase tracking-wider whitespace-nowrap">{tp.filterProvider}</span>
              <div className="relative">
                <select
                  value={provider}
                  onChange={e => setProvider(e.target.value === 'all' ? 'all' : Number(e.target.value))}
                  data-testid="filter-provider-select"
                  className="appearance-none pl-3 pr-8 py-1.5 bg-slate-800 border border-slate-700 rounded-full text-xs text-slate-300 focus:outline-none focus:border-amber-500 transition-colors cursor-pointer hover:border-slate-500"
                  style={{ colorScheme: 'dark' }}
                >
                  <option value="all">{tp.filterAllProviders}</option>
                  {usedProviders.map(prov => (
                    <option key={prov.id} value={prov.id}>{prov.name}</option>
                  ))}
                </select>
                <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-500" />
              </div>
            </div>
          )}

          {(category !== 'all' || provider !== 'all' || search) && (
            <button onClick={() => { setCategory('all'); setProvider('all'); setSearch(''); }}
              data-testid="clear-filters"
              className="px-3 py-1 rounded-full text-xs text-slate-500 hover:text-white transition-colors whitespace-nowrap">
              {tp.clearFilters} ×
            </button>
          )}

          {/* Full-content toggle */}
          <label className="flex items-center gap-2 cursor-pointer select-none ml-auto"
            data-testid="toggle-full-content">
            <div className="relative">
              <input
                type="checkbox"
                checked={fullContent}
                onChange={e => setFullContent(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-8 h-4 rounded-full bg-slate-700 peer-checked:bg-cyan-500 transition-colors" />
              <div className="absolute left-0.5 top-0.5 w-3 h-3 rounded-full bg-white transition-transform peer-checked:translate-x-4" />
            </div>
            <span className="text-xs text-slate-400 whitespace-nowrap">{tp.showFullContent}</span>
          </label>
        </div>
      </div>

      {/* Table */}
      <main className="flex-1 py-8 px-4 md:px-8 max-w-7xl mx-auto w-full">
        <div className="w-full overflow-x-auto rounded-xl border border-slate-800 bg-[#0f172a]/50 backdrop-blur-sm">
          {loading ? (
            <div className="flex items-center justify-center py-20 gap-3" data-testid="table-loading">
              <Loader2 className="w-5 h-5 text-cyan-400 animate-spin" />
              <span className="text-slate-400">{tp.loadingProducts}</span>
            </div>
          ) : (
            <table className="w-full min-w-[955px] table-fixed" data-testid="products-table">
              <thead>
                <tr className="border-b border-slate-800">

                  {/* Product – sticky */}
                  <th className="sticky left-0 bg-[#0f172a] z-10 px-3 py-3 text-left w-[120px]">
                    <button onClick={() => handleSort('name')} data-testid="sort-name"
                      className={`flex items-center gap-1 text-xs font-semibold uppercase tracking-wider transition-colors ${
                        sort.key === 'name' ? 'text-cyan-400' : 'text-slate-400 hover:text-white'
                      }`}>
                      {tp.thProduct}<SortIcon col="name" cfg={sort} />
                    </button>
                  </th>

                  {/* Provider */}
                  <th className="px-2 py-3 text-left w-[88px]">
                    <button onClick={() => handleSort('provider_name')} data-testid="sort-provider"
                      className={`flex items-center gap-1 text-xs font-semibold uppercase tracking-wider transition-colors ${
                        sort.key === 'provider_name' ? 'text-cyan-400' : 'text-slate-400 hover:text-white'
                      }`}>
                      {tp.thProvider}<SortIcon col="provider_name" cfg={sort} />
                    </button>
                  </th>

                  <Th label={tp.thPrice}     col="price"        className="w-[90px]" />
                  <Th label={tp.thCpu}       col="cpu_cores"    className="w-[90px]" />
                  <Th label={tp.thRam}       col="ram_size"     className="w-[80px]" />
                  <Th label={tp.thStorage}   col="storage_size" className="w-[90px]" />
                  <Th label={tp.thBandwidth} col="bandwidth"    className="w-[75px]" />
                  <Th label={tp.thTraffic}   col="traffic"      className="w-[70px]" />

                  <th className="px-3 py-3 text-left w-[105px]">
                    <span className="text-xs font-semibold uppercase tracking-wider text-slate-400 whitespace-nowrap">{tp.thDdos}</span>
                  </th>
                  <th className="px-3 py-3 text-right w-[155px]">
                    <span className="text-xs font-semibold uppercase tracking-wider text-slate-400 whitespace-nowrap">{tp.thActions}</span>
                  </th>
                </tr>
              </thead>

              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="py-20 text-center text-slate-500" data-testid="no-products">
                      <Tag className="w-8 h-8 text-slate-700 mx-auto mb-3" />
                      <p>{tp.noProducts}</p>
                    </td>
                  </tr>
                ) : paginated.map((p, idx) => (
                  <tr key={p.id} data-testid={`product-row-${idx}`}
                    onClick={() => navigate(`/anbieter/${p.hosting_id}/produkt/${p.id}`)}
                    className="border-b border-slate-800/60 hover:bg-cyan-500/[0.04] transition-colors group cursor-pointer">

                    {/* Product name – sticky */}
                    <td className="sticky left-0 bg-[#0f172a] group-hover:bg-[#0d1f38] transition-colors px-3 py-3.5 z-10 overflow-hidden">
                      <div className="overflow-hidden">
                        <div
                          data-testid={`product-name-${idx}`}
                          className={`font-semibold text-white group-hover:text-cyan-400 transition-colors text-sm ${
                            fullContent ? 'break-words' : 'truncate'
                          }`}>
                          {p.name}
                        </div>
                        <div className="hidden sm:flex items-center gap-1.5 mt-0.5">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-800 border border-slate-700 text-slate-400 whitespace-nowrap">
                            {t.categories?.[p.category] || p.category}
                          </span>
                        </div>
                        {p.description && (
                          <p className="hidden sm:block text-[11px] text-slate-500 mt-0.5 truncate">
                            {p.description}
                          </p>
                        )}
                      </div>
                    </td>

                    {/* Provider */}
                    <td className="px-2 py-3.5 overflow-hidden" onClick={e => e.stopPropagation()}>
                      <Link to={`/anbieter/${p.hosting_id}`}
                        className="flex items-center gap-1.5 group/prov">
                        <span className="hidden sm:block flex-shrink-0">
                          <ProviderLogo name={p.provider_name} logo={p.provider_logo} />
                        </span>
                        <span className="text-xs text-slate-300 group-hover/prov:text-cyan-400 transition-colors truncate block">
                          {p.provider_name}
                        </span>
                      </Link>
                    </td>

                    {/* Price */}
                    <td className="px-3 py-3.5 overflow-hidden">
                      <span className={`text-sm font-semibold ${Number(p.price) > 0 ? 'text-cyan-400' : 'text-green-400'}`}>
                        {Number(p.price) > 0 ? `${Number(p.price).toFixed(2)} €` : tp.free}
                      </span>
                      {Number(p.price) > 0 && p.price_period && (
                        <span className="text-[11px] text-slate-500 ml-1 block">
                          {tp.period?.[p.price_period] || p.price_period}
                        </span>
                      )}
                    </td>

                    {/* CPU */}
                    <td className="px-3 py-3.5 overflow-hidden">
                      <div className="text-xs break-words"><Cell val={p.cpu_cores} /></div>
                      {p.cpu_type && <div className="text-[11px] text-slate-500 mt-0.5 break-words">{p.cpu_type}</div>}
                    </td>

                    {/* RAM */}
                    <td className="px-3 py-3.5 overflow-hidden">
                      <div className="text-xs break-words"><Cell val={p.ram_size} /></div>
                      {p.ram_type && <div className="text-[11px] text-slate-500 mt-0.5 break-words">{p.ram_type}</div>}
                    </td>

                    {/* Storage */}
                    <td className="px-3 py-3.5 overflow-hidden">
                      <div className="text-xs break-words"><Cell val={p.storage_size} /></div>
                      {p.storage_type && <div className="text-[11px] text-slate-500 mt-0.5 break-words">{p.storage_type}</div>}
                    </td>

                    {/* Bandwidth */}
                    <td className="px-3 py-3.5 overflow-hidden">
                      <div className="text-xs break-words"><Cell val={p.bandwidth} /></div>
                    </td>

                    {/* Traffic */}
                    <td className="px-3 py-3.5 overflow-hidden">
                      <div className="text-xs break-words"><Cell val={p.traffic} /></div>
                    </td>

                    {/* DDoS */}
                    <td className="px-3 py-3.5 overflow-hidden">
                      <div className="text-xs break-words"><Cell val={p.ddos_protection} /></div>
                    </td>

                    {/* Actions */}
                    <td className="px-3 py-3.5 text-right" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-2">
                        {p.url ? (
                          <a href={p.url} target="_blank" rel="noopener noreferrer"
                            data-testid={`offer-btn-${idx}`}
                            onClick={e => e.stopPropagation()}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 rounded-lg text-xs font-semibold hover:bg-cyan-500 hover:text-slate-950 hover:border-cyan-500 transition-colors whitespace-nowrap">
                            {tp.toProduct}<ExternalLink className="w-3 h-3 flex-shrink-0" />
                          </a>
                        ) : (
                          <span className="text-xs text-slate-600 italic">–</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Sort tip */}
        <p className="mt-3 text-xs text-slate-600 text-center">{tp.sortTip}</p>

        {/* Pagination */}
        {filtered.length > 0 && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 px-1"
            data-testid="pagination">

            {/* Info + per-page selector */}
            <div className="flex items-center gap-3 text-xs text-slate-400">
              <span data-testid="page-info">
                {tp.pageInfo(
                  Math.min(pageFrom + 1, filtered.length),
                  Math.min(pageFrom + perPage, filtered.length),
                  filtered.length
                )}
              </span>
              <select
                value={perPage}
                onChange={e => { setPerPage(Number(e.target.value)); setPage(1); }}
                data-testid="per-page-select"
                className="bg-slate-800 border border-slate-700 text-slate-300 rounded-lg px-2 py-1 text-xs cursor-pointer hover:border-slate-600 transition-colors">
                {[10, 25, 50].map(n => (
                  <option key={n} value={n}>{n} {tp.perPage}</option>
                ))}
              </select>
            </div>

            {/* Page buttons */}
            {totalPages > 1 && (
              <div className="flex items-center gap-1" data-testid="page-buttons">
                {/* Prev */}
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={safePage === 1}
                  data-testid="page-prev"
                  className="p-1.5 rounded-lg border border-slate-700 text-slate-400 hover:text-white hover:border-slate-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                  <ChevronLeft className="w-3.5 h-3.5" />
                </button>

                {/* Page numbers */}
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(n => n === 1 || n === totalPages || Math.abs(n - safePage) <= 1)
                  .reduce((acc, n, i, arr) => {
                    if (i > 0 && n - arr[i - 1] > 1) acc.push('…');
                    acc.push(n);
                    return acc;
                  }, [])
                  .map((n, i) =>
                    n === '…' ? (
                      <span key={`gap-${i}`} className="px-1 text-slate-600 text-xs">…</span>
                    ) : (
                      <button key={n}
                        onClick={() => setPage(n)}
                        data-testid={`page-btn-${n}`}
                        className={`min-w-[28px] h-7 rounded-lg text-xs font-medium border transition-colors ${
                          safePage === n
                            ? 'bg-cyan-500 border-cyan-500 text-slate-950'
                            : 'border-slate-700 text-slate-400 hover:text-white hover:border-slate-500'
                        }`}>
                        {n}
                      </button>
                    )
                  )}

                {/* Next */}
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={safePage === totalPages}
                  data-testid="page-next"
                  className="p-1.5 rounded-lg border border-slate-700 text-slate-400 hover:text-white hover:border-slate-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
